import ServiceLocator from "../../../../global/service-locator";
import ModalFormGroup from "../../../../components/base/form-v2/modal-form-group";
import CheckoutAuthenticatedFooterBlockElement from "./elements/auth-footer-block-element";
import LmsRadioFieldElement from "../../../../components/base/form-v2/elements/field-types/lms-radio-field-element";
import PaymentFormatFieldElement
    from "../../../../components/base/form-v2/elements/field-types/payment-format-field-element";
import LocalizationsHelper from "../../../../lib/localizations-helper";
import CheckoutService from "./checkout-service";
import DateHelper from "../../../../lib/date-helper";
import {debounce, declension} from "../../../../lib";
import FormEventSubscription from "../../../../components/base/form-v2/form-event-subscription";
import PaymentInfoModel from "./models/payment-info-model";
import CheckoutSidebarPriceListElement from "./elements/checkout-sidebar-price-list-element";
import StringHelper from "../../../../lib/string-helper";
import LmsCheckboxFieldElement
    from "../../../../components/base/form-v2/elements/field-types/lms-checkbox-field-element";
import AgPayButtonService from "./components/ag-pay-button-service";
import craft from "../../../../lib/env-helper";
import Api from "../../../../lib/api";
import ObjectHelper from "../../../../lib/object-helper";
import SocketService from "./socket-service";
import {JOIN_TO_PAYMENT_SOCKET_EVENT, LEAVE_FROM_PAYMENT_SOCKET_EVENT, PAYMENT_STATUS_SOCKET_EVENT} from "./constants";
import DOMElement from "../../../../components/base/dom-element";

const BALANCE_TYPES = ['money', 'bonus', 'referral'];

export default class CheckoutModal {
    constructor() {
        this.$root = document.getElementById('modal-checkout');
        this.sl = ServiceLocator.instance;

        this._courseId = craft.page.coursesStatsConfig.scheduleId;
        this._comingGroupsInfo = null;
        /** @type {PaymentInfoModel} */
        this._paymentInfo = null;
        this._priceParams = null;
        this._lmsUser = null;
        this._initialized = false;
        this._orderId = null;

        if (this.$root) {
            this._init();
        }
    }

    _init() {
        this._initElements();
        this._addSubscriptions();
        if (this.sl.userDataService.lmsUser) {
            this._bootstrap();
        }
    }

    _initSocket() {
        SocketService.instance.connect();

        if (this._orderId) {
            const form = this.formGroup.getForm('checkout')
            if (form.activeModuleName === 'mono-request' || form.activeModuleName === 'pumb-request') {
                SocketService.instance.emit(JOIN_TO_PAYMENT_SOCKET_EVENT, this._orderId);
                SocketService.instance.on(PAYMENT_STATUS_SOCKET_EVENT, this._handlePaymentStatus.bind(this));
                CheckoutService.getSiteOrderResult(this._orderId).then((res) => this._handlePaymentStatus(res.response));
            }
        }
    }

    _disconnectSocket() {
        if (this._orderId) {
            SocketService.instance.emit(LEAVE_FROM_PAYMENT_SOCKET_EVENT, this._orderId);
        }

        SocketService.instance.off(PAYMENT_STATUS_SOCKET_EVENT);
        SocketService.instance.disconnect();
    }

    _addSubscriptions() {
        window.addEventListener(this.sl.userDataService.LMS_USER_DATA_LOADED_EVENT, (e) => {
            this._bootstrap();
        });
    }

    _initElements() {
        this.$formGroupRoot = this.$root.querySelector('[data-form-group="root"]');
        this.$checkoutLoginFormItem = document.querySelector('[data-form-group="item"][data-name="checkout-login"]');
        this.$checkoutLoginFormFooter = this.$checkoutLoginFormItem.querySelector('.f-footer');
        this.$checkoutForm = document.getElementById('form-checkout');
        this.$mainModule = this.$checkoutForm.querySelector('[data-form="module"][data-name="main"]');
        this.$registrationModule = this.$checkoutForm.querySelector('[data-form="module"][data-name="registration"]');
        this.$mainModuleHeader = this.$mainModule.querySelector('.f-checkout-header');
        this.$mainModuleDesktopFooter = this.$mainModule.querySelector('.checkout-form-footer.-desktop');
        this.$mainModuleMobileFooter = this.$mainModule.querySelector('.checkout-form-footer.-mobile');
        this.$groupField = this.$mainModule.querySelector('[data-form="field"][data-name="group"]');
        this.$promocodeField = this.$mainModule.querySelector('[data-form="field"][data-name="promocode"]');
        this.$paymentFormatField = this.$mainModule.querySelector('[data-form="field"][data-name="payment-format"]');
        this.$balancesField = this.$mainModule.querySelector('[data-form="field"][data-name="balances"]');
        this.$checkoutPriceList = this.$mainModule.querySelector('.checkout-price-list');
        this.$ssoSignInGoogle = document.getElementById('sso-signin-google');
        this.$ssoSignInApple = document.getElementById('sso-signin-apple');

        this._updateSsoButtons();
    }

    _updateSsoButtons() {
        if (this.$ssoSignInGoogle) {
            this.$ssoSignInGoogle.href = `${Api.lmsApiUrl}/users/google/signin?is_site=true&referrer=${location.href}`;
        }

        if (this.$ssoSignInApple) {
            this.$ssoSignInApple.href = `${Api.lmsApiUrl}/users/google/signin?is_site=true&referrer=${location.href}`;
        }
    }

    _bootstrap() {
        if (this._initialized) {
            return;
        }

        this._initialized = true;
        const forms = [
            {
                id: 'checkout',
                path: '/',
                data: {
                    referrer: location.href
                },
                subscriptions: [
                    new FormEventSubscription({
                        module: 'main',
                        event: 'fieldValueChange',
                        callback: debounce(this._onMainModuleFieldValueChange.bind(this), 50)
                    }),
                    new FormEventSubscription({
                        event: 'submitSuccess',
                        callback: this._onCheckoutSubmitSuccess.bind(this)
                    }),
                    new FormEventSubscription({
                        event: 'submitFailure',
                        callback: this._onCheckoutSubmitFailure.bind(this)
                    }),
                    new FormEventSubscription({
                        event: 'activeModuleChange',
                        callback: this._onCheckoutFormActiveModuleChange.bind(this)
                    })
                ],
                fieldOptions: {
                    balances: {
                        altDataType: true
                    }
                },
                submitDataTransformer: this._prepareDataForSubmit.bind(this)
            }
        ];

        this._checkUserAndUpdate();

        if (!this._lmsUser) {
            forms.push({
                id: 'checkout-login',
                baseUrl: Api.lmsApiUrl,
                path: `/users/login`,
                subscriptions: [
                    new FormEventSubscription({
                        event: 'fieldValueChange',
                        callback: () => {
                            this.$checkoutLoginFormFooter.removeAttribute('data-error');
                        }
                    }),
                    new FormEventSubscription({
                        event: 'submitAnimationComplete',
                        callback: () => {
                            this.formGroup.changeView('loading');
                            this.sl.userDataService.fetchLMSUserData()
                                .then(() => {
                                    this._checkUserAndUpdate();
                                    this.agPayButtonService.setupPayButtons(false, true);

                                    setTimeout(() => {
                                        this.formGroup.changeView('checkout');
                                    }, 500);
                                });
                        }
                    }),
                    new FormEventSubscription({
                        event: 'submitFailure',
                        callback: (res) => {
                            const status = ObjectHelper.get(res, 'response.status');

                            if (status === 401) {
                                this.$checkoutLoginFormFooter.dataset.error = 'unauthorized';
                            } else {
                                this.$checkoutLoginFormFooter.dataset.error = 'unknown';
                            }
                        }
                    })
                ]
            });
        }

        CheckoutService.getComingGroupsInfo(this._courseId).then((comingGroupsInfoResponse) => {
            this._comingGroupsInfo = comingGroupsInfoResponse.response;

            if (!this._comingGroupsInfo.video_course && !this._comingGroupsInfo.groups.length) {
                throw new Error('User Already In Participants');
            }

            return CheckoutService.getPaymentInfo(this._comingGroupsInfo.video_course || this._comingGroupsInfo.groups[0]._id, {
                balance_types: BALANCE_TYPES,
                payment_format: 'full'
            });
        }).then((paymentInfoResponse) => {
            this.agPayButtonService = new AgPayButtonService({
                $root: this.$root,
                onPaymentTokenReceived: this._onAGPayTokenReceived.bind(this),
                onPaymentCancel: this._onAGPaymentError.bind(this),
                onPaymentError: this._onAGPaymentError.bind(this),
                subscriptions: [
                    {
                        event: 'payButtonClicked',
                        callback: this._onAGPayButtonClicked.bind(this)
                    }
                ]
            });
            this._paymentInfo = new PaymentInfoModel(paymentInfoResponse.response);
            this._updateAGPayButton();

            try {
                this.formGroup = new ModalFormGroup({
                    id: 'checkout',
                    urlHashes: [
                        'checkout',
                        'checkout-sso-failed',
                        'checkout-sso-failed-unauthorized'
                    ],
                    forms,
                    beforeInit: () => {
                        this._updateGroupField();
                        this._handlePaymentInfoChange(this._paymentInfo, ['prices', 'balances', 'payment-format']);
                    },
                    afterInit: () => setTimeout(() => {
                        if (
                            this.$checkoutLoginFormItem &&
                            this.$checkoutLoginFormFooter &&
                            new RegExp('^checkout-sso-failed').test(location.hash.slice(1))
                        ) {
                            if (new RegExp('^checkout-sso-failed$').test(location.hash.slice(1))) {
                                this.$checkoutLoginFormFooter.dataset.error = 'unknown';
                            } else if (new RegExp('^checkout-sso-failed-unauthorized$').test(location.hash.slice(1))) {
                                this.$checkoutLoginFormFooter.dataset.error = 'sso-failed-unauthorized';
                            }

                            this.formGroup.changeView('checkout-login');
                            return;
                        }

                        this.formGroup.changeView('checkout');
                    }, 0),
                    beforeOpen: () => {
                        this._initSocket();
                        this._updatePaymentInfo();
                    },
                    beforeClose: () => {
                        this._disconnectSocket();
                    }
                });
            } catch (error) {
                console.error(error);
            }
        }).catch((error) => {
            console.error(error);

            if (!this.formGroup) {
                this.formGroup = new ModalFormGroup({
                    id: 'checkout',
                    forms: [{ id: 'checkout-error', path: '/' }],
                    afterInit: () => setTimeout(() => {
                        this.$formGroupRoot.classList.add('-payment-result');
                        this.formGroup.changeView('checkout-error');

                        if (ObjectHelper.get(error, 'response.data.errors.0') === 'User Already In Participants' ||
                            ObjectHelper.get(error, 'message') === 'User Already In Participants') {
                            this.formGroup.getForm('checkout-error').changeModule('already-in-participants');
                            return;
                        }

                        this.formGroup.getForm('checkout-error').changeModule('unknown-error');
                    }, 0)
                });

                return;
            }

            this.formGroup.getForm('checkout').changeModule('unknown-error');
        });
    }

    _onCheckoutFormActiveModuleChange({ name, prevName }) {
        this.$formGroupRoot.classList.remove('-payment-result');

        if ([
            'mono-request',
            'pumb-request',
            'payment-success',
            'payment-rejection',
            'payment-failure',
            'already-in-participants',
            'unknown-error'
        ].includes(name)) {
            this.$formGroupRoot.classList.add('-payment-result');
        }

        SocketService.instance.off(PAYMENT_STATUS_SOCKET_EVENT);

        if (name === 'mono-request' || name === 'pumb-request') {
            SocketService.instance.on(PAYMENT_STATUS_SOCKET_EVENT, this._handlePaymentStatus.bind(this));
        }

        if ((prevName === 'mono-request' || prevName === 'pumb-request') && name === 'main' && this._orderId) {
            if (name === 'main') {
                CheckoutService.cancelSiteOrder(this._orderId);
            }

            SocketService.instance.emit(LEAVE_FROM_PAYMENT_SOCKET_EVENT, this._orderId);
            this._orderId = null;
        }
    }

    _handlePaymentStatus(data) {
        if (data.status === 'success') {
            const $link = this.$checkoutForm.querySelector('[data-form="module"][data-name="payment-success"] .checkout-result-button');

            if (!$link) {
                return;
            }

            if (this._lmsUser) {
                $link.href = `${Api.lmsUrl}/finances`;
                $link.textContent = LocalizationsHelper.get('component.checkout.goToFinance');
            } else {
                $link.href = data.redirect_url || `${Api.lmsUrl}`;
                $link.textContent = LocalizationsHelper.get('component.checkout.continueRegistration');
            }

            this.formGroup.getForm('checkout').changeModule('payment-success');
        } else if (data.status === 'failed') {
            this.formGroup.getForm('checkout').changeModule('payment-rejection');
        }
    }

    _checkUserAndUpdate() {
        const lmsUser = this.sl.userDataService.lmsUser;

        if (!lmsUser) {
            return;
        }

        this._lmsUser = lmsUser;
        this._updateHeader({
            name: lmsUser.fullName,
            avatarSrc: lmsUser.avatarUrl
        });
        this._updateFooter();
        this.$registrationModule.remove();
        this.$registrationModule = null;
        this.$checkoutLoginFormItem.remove();
        this.$checkoutLoginFormItem = null;
    }

    _onAGPayButtonClicked(e) {
        this.agPayButtonService.setupPayButtons(true);
        this.formGroup.getForm('checkout').disableSubmitButton();
    }

    _onAGPayTokenReceived(token, type) {
        if (!this.formGroup) {
            return;
        }

        const form = this.formGroup.getForm('checkout');

        if (!form) {
            return;
        }

        const submitData = form.getSubmitData();

        if (type === 'apple') {
            submitData.body.apay_token = token;
        } else {
            submitData.body.gpay_token = token;
        }

        Api.post(submitData.url, submitData.body, submitData.query)
            .then(this._onCheckoutSubmitSuccess.bind(this))
            .then(() => {
                this.formGroup.getForm('checkout').enableSubmitButton();
            }).catch((e) => {
                this.agPayButtonService.setupPayButtons();
                this.formGroup.getForm('checkout').enableSubmitButton();
            });
    }

    _onAGPaymentError() {
        this.agPayButtonService.setupPayButtons();
        this.formGroup.getForm('checkout').enableSubmitButton();
    }

    _onCheckoutSubmitSuccess(response) {
        if (ObjectHelper.get(response, 'data.response.redirect_url')) {
            location.href = ObjectHelper.get(response, 'data.response.redirect_url');
            return;
        } else if (!ObjectHelper.get(response, 'data.response.order')) {
            return;
        }

        const order = ObjectHelper.get(response, 'data.response.order');

        if (order.system === 'monobank_payparts' || order.system === 'pumb_payparts') {
            this._orderId = order._id;
            SocketService.instance.emit(JOIN_TO_PAYMENT_SOCKET_EVENT, this._orderId);

            if (order.system === 'monobank_payparts') {
                this.formGroup.getForm('checkout').changeModule('mono-request');
            } else if (order.system === 'pumb_payparts') {
                this.formGroup.getForm('checkout').changeModule('pumb-request');
            }

        } else if (order.payment_link) {
            location.href = order.payment_link;
            this.agPayButtonService.setupPayButtons();
        }
    }

    _onCheckoutSubmitFailure(error) {
        const errorMessage = ObjectHelper.get(error, 'response.data.errors.0');

        if (!errorMessage || errorMessage !== 'User Already In Participants') {
            this.formGroup.getForm('checkout').changeModule('unknown-error');
            return;
        }

        this.formGroup.getForm('checkout').changeModule('already-in-participants');
    }

    _prepareDataForSubmit(data) {
        const body = {
            origin: 'site',
            payment_format: data.body['payment-format'].type
        };

        if (this._lmsUser) {
            body.email = this._lmsUser.email;
            body.first_name = this._lmsUser.firstName;
            body.last_name = this._lmsUser.lastName;
        } else {
            body.first_name = data.body.name;
            body.last_name = data.body.lastName;
            body.email = data.body.email;
        }

        if (data.body['payment-format'].payments) {
            body.payparts_count = data.body['payment-format'].payments;
        }

        if (data.body['payment-format'].tel) {
            body.payparts_phone = data.body['payment-format'].tel;
        }

        if (data.body.promocode && data.body.promocode.applied) {
            body.coupon = data.body.promocode.code;
        }

        if (data.body.balances) {
            body.balance_types = BALANCE_TYPES.filter((balanceType) =>
                data.body.balances[balanceType] == null || data.body.balances[balanceType] === true)
        }

        const groupId = this._comingGroupsInfo.video_course || data.body.group;

        Object.assign(data, {
            url: `${Api.lmsApiUrl}/groups/${groupId}/site-buy`,
            body
        });
        return data;
    }

    _onMainModuleFieldValueChange({ name, value }) {
        const checkoutForm = this.formGroup.getForm('checkout');

        if (!checkoutForm) {
            console.warn('Checkout modal: _onMainModuleFieldValueChange: Checkout form not found');
            return;
        }

        if (name === 'promocode' && value && value.applied) {
            return;
        }

        let paymentFormatValue = checkoutForm.getFieldValue('payment-format');
        const balancesValue = checkoutForm.getFieldValue('balances');
        const promocodeValue = checkoutForm.getFieldValue('promocode');

        if (paymentFormatValue) {
            paymentFormatValue = { ...paymentFormatValue };
            delete paymentFormatValue.tel;
        }

        let balanceTypes = BALANCE_TYPES;

        if (balancesValue) {
            balanceTypes = BALANCE_TYPES.filter((balanceType) =>
                balancesValue[balanceType] == null || balancesValue[balanceType] === true);
        }

        const params = {
            balance_types: balanceTypes
        };

        if (paymentFormatValue && paymentFormatValue.type) {
            params.payment_format = paymentFormatValue.type;
        }

        if (paymentFormatValue && paymentFormatValue.payments) {
            params.payparts_count = paymentFormatValue.payments;
        }

        if (promocodeValue) {
            params.coupon = promocodeValue.code;
        }

        if (name !== 'group' && JSON.stringify(this._priceParams) === JSON.stringify(params)) {
            return;
        }

        this._priceParams = params;
        this._updatePaymentInfo(name);
    }

    _updatePaymentInfo(name) {
        const checkoutForm = this.formGroup.getForm('checkout');

        CheckoutService.getPaymentInfo(this._comingGroupsInfo.video_course || checkoutForm.getFieldValue('group'), this._priceParams)
            .then((paymentInfoResponse) => {
                const newPaymentInfo = new PaymentInfoModel(paymentInfoResponse.response);
                const update = [];

                if (!this._paymentInfo.equals(newPaymentInfo)) {
                    update.push('prices');
                }

                if (['group', 'payment-format', 'promocode', 'balances'].includes(name) &&
                    !this._paymentInfo.equalBalances(newPaymentInfo)) {
                    update.push('balances');
                }

                if (name === 'group' && !this._paymentInfo.equalPaymentFormats(newPaymentInfo) || !this._paymentInfo.equalPaymentFormatList(newPaymentInfo)) {
                    update.push('payment-format');
                }


                if (name === 'promocode' && newPaymentInfo.getCouponDiscount()) {
                    update.push('promocode');
                }

                this._handlePaymentInfoChange(newPaymentInfo, update);
                this._paymentInfo = newPaymentInfo;
                this._updateAGPayButton();
            })
            .catch((e) => {
                if (!ObjectHelper.get(e, 'response.data.errors.length')) {
                    return;
                }

                const error = e.response.data.errors[0];
                let message = '';

                if (error.coupon) {
                    message = (typeof error.coupon === 'object') && error.coupon.message ? error.coupon.message : error.coupon;
                }

                const form = this.formGroup.getForm('checkout');

                if (!form) {
                    return;
                }

                form.getField('promocode').setError(message);
            });
    }

    /**
     * @param {PaymentInfoModel} paymentInfo
     * @param {string[]} update
     */
    _handlePaymentInfoChange(paymentInfo, update = []) {
        if (update.includes('prices')) {
            this._updateCheckoutPrice(paymentInfo);
        }

        if (update.includes('balances')) {
            this._updateBalancesField(paymentInfo, true);
        }

        if (update.includes('payment-format')) {
            this._updatePaymentFormatField(paymentInfo, true);
        }

        if (update.includes('promocode')) {
            this._updatePromoCodeField(paymentInfo);
        }
    }

    /**
     * @param {PaymentInfoModel} paymentInfo
     */
    _updateCheckoutPrice(paymentInfo) {
        const { allDiscounts, coupon } = paymentInfo.allDiscounts.reduce((acc, discount) => {
            if (discount.type === 'coupon') {
                acc.coupon = Number(discount.value);
            } else {
                acc.allDiscounts.push(discount);
            }

            return acc;
        }, { allDiscounts: [], coupon: 0 });
        let appliedDiscounts = paymentInfo.appliedDiscounts - coupon;
        appliedDiscounts = appliedDiscounts < 0 ? 0 : appliedDiscounts;

        const updatedCheckoutPrice = new CheckoutSidebarPriceListElement({
            initialPrice: { name: LocalizationsHelper.get('component.checkout.fullCourseCost'), amount: this._transformAmount(paymentInfo.initialCost) },
            items: [
                // Applied discounts
                ...(!!appliedDiscounts ? [{
                    name: LocalizationsHelper.get('component.checkout.discountsApplied'),
                    amount: `-${this._transformAmount(appliedDiscounts)}`,
                    sublist: allDiscounts.map((discount) => ({
                        label: this._getDiscountLabel(discount.type, discount.value)
                    }))
                }] : []),
                // Coupon
                ...(!!coupon ? [{ name: LocalizationsHelper.get('component.checkout.promocode'), amount: `-${this._transformAmount(coupon)}` }] : []),
                // Balances
                ...(paymentInfo.balances ? Object.keys(paymentInfo.balances).reduce((acc, balanceType) => {
                    if (!paymentInfo.balances[balanceType].selected) {
                        return acc;
                    }

                    acc.push({
                        name: `${LocalizationsHelper.get('component.checkout.writeOff')} ${LocalizationsHelper.get(`component.checkout.balancesField.option.${balanceType}`)}`,
                        amount: `-${this._transformAmount(paymentInfo.balances[balanceType].amount)}`
                    });
                    return acc;
                }, []) : []),
                // Frozen money
                ...(paymentInfo.freezeMoney ? [{ name: LocalizationsHelper.get('component.checkout.frozenMoney'), amount: `-${this._transformAmount(paymentInfo.freezeMoney)}` }] : []),
                // Divided cost
                ...(!!paymentInfo.dividedCost ? [{
                    name: `${LocalizationsHelper.get('component.checkout.dividedCost.divided')} ${paymentInfo.paymentsNumber} ${LocalizationsHelper.get('component.checkout.dividedCost.parts')}`,
                    amount: this._transformAmount(paymentInfo.dividedCost)
                }] : []),
                // Payparts
                ...(paymentInfo.payparts ? [
                    {
                        name: LocalizationsHelper.get('component.checkout.payparts.count'),
                        amount: paymentInfo.payparts.parts
                    },
                    {
                        name: LocalizationsHelper.get('component.checkout.payparts.monthly'),
                        amount: this._transformAmount(paymentInfo.payparts.cost)
                    }
                ] : [])
            ],
            finalPrice: { name: LocalizationsHelper.get('component.checkout.totalCost'), amount: this._transformAmount(paymentInfo.finalCost) },
        });
        this.$checkoutPriceList.replaceWith(updatedCheckoutPrice.getElement());
        this.$checkoutPriceList = updatedCheckoutPrice.getElement();
    }

    _transformAmount(amount) {
        return `${StringHelper.separateThousands(amount)} ₴`;
    }

    _getDiscountLabel(type, value) {
        switch (type) {
            case 'full':
                return `${value}% ${LocalizationsHelper.get('component.checkout.forFullPayment')}`;
            default:
                return '';
        }
    }

    _updateHeader({ name, avatarSrc }) {
        if (!this.$userAvatar) {
            this.$userAvatar = this.$mainModuleHeader.querySelector('.f-checkout-user-avatar');
        }

        if (!this.$userName) {
            this.$userName = this.$mainModuleHeader.querySelector('.f-checkout-user-name');
        }

        if (!this.$loginButton) {
            this.$loginButton = this.$mainModuleHeader.querySelector('[data-form-group="next"][data-name="checkout-login"]');
            this.$loginButton.remove();
            this.$loginButton = null;
        }

        this.$userAvatar.src = avatarSrc;
        this.$userName.textContent = name;
    }

    _updateFooter() {
        if (this.$mainModuleDesktopFooter.dataset.type === 'checkout-auth') {
            return;
        }

        const updatedDesktopFooter = new CheckoutAuthenticatedFooterBlockElement({ class: '-desktop' }).getElement();
        const updatedMobileFooter = new CheckoutAuthenticatedFooterBlockElement({ class: '-mobile' }).getElement();
        this.$mainModuleDesktopFooter.replaceWith(updatedDesktopFooter);
        this.$mainModuleDesktopFooter = updatedDesktopFooter;
        this.$mainModuleMobileFooter.replaceWith(updatedMobileFooter);
        this.$mainModuleMobileFooter = updatedMobileFooter;
    }

    _updateGroupField() {
        if (this._comingGroupsInfo.video_course) {
            this.$groupField.remove();
            this.$groupField = null;
            return;
        }

        const updatedGroupField = new LmsRadioFieldElement({
            id: 'field-group-checkout',
            inputId: 'input-group-checkout',
            class: 'f-module_field',
            name: 'group',
            label: LocalizationsHelper.get('component.checkout.selectGroup'),
            required: true,
            options: this._getGroupOptions(this._comingGroupsInfo.groups)
        }).getElement();
        this.$groupField.replaceWith(updatedGroupField);
        this.$groupField = updatedGroupField;
    }

    /**
     * @param {PaymentInfoModel} paymentInfo
     * @param {boolean} reinit
     */
    _updatePaymentFormatField(paymentInfo, reinit = false) {
        const updatedPaymentFormatField = new PaymentFormatFieldElement({
            id: 'field-payment-format-checkout',
            inputId: 'input-payment-format-checkout',
            class: 'f-module_field',
            name: 'payment-format',
            required: true,
            options: paymentInfo.paymentFormats.map((format) => {
                const result = { value: format };

                if (['privatbank_payparts', 'monobank_payparts', 'pumb_payparts'].includes(format)) {
                    result.tel = this._lmsUser ? this._lmsUser.phone : '';
                    result.selectValues = paymentInfo.paypartsOptions[format] || [];
                } else if (format === 'partial') {
                    result.paymentsNumber = paymentInfo.paymentsNumber;
                }

                if (format === paymentInfo.paymentFormat) {
                    result.checked = true;
                }

                return result;
            })
        }).getElement();
        this.$paymentFormatField.replaceWith(updatedPaymentFormatField);
        this.$paymentFormatField = updatedPaymentFormatField;

        if (reinit && this.formGroup) {
            const form = this.formGroup.getForm('checkout');

            if (form) {
                form.reinitField('payment-format', this.$paymentFormatField);
            }
        }
    }

    _getGroupOptions(data) {
        return data.map((group, index) => {
            const moment = DateHelper.moment({
                value: group.start_date,
            });

            let placesLeft = group.seats ?
                ` <span class="f-badge -sm -red">${group.seats} ${declension(group.seats, LocalizationsHelper.get('places'))} ${LocalizationsHelper.get('component.checkout.remaining')}</span>` : '';

            if (group.seats && group.limitless_bonuses) {
                placesLeft = ` <span class="f-badge -sm -violet">${group.seats} ${LocalizationsHelper.get('component.checkout.promotional')} ${declension(group.seats, LocalizationsHelper.get('places'))} ${LocalizationsHelper.get('component.checkout.remaining')}</span>`
            }

            return {
                value: group._id,
                label: `${LocalizationsHelper.get('component.checkout.start')} ${moment.format('d MMM')}${placesLeft}`,
                caption: group.days.reduce((acc, day) => {
                    if (acc.length) {
                        acc += ', ';
                    }

                    const timeFrom = DateHelper.moment({
                        value: day.time
                    });
                    const timeTo = DateHelper.moment({
                        value: new Date(day.time).getTime() + group.lesson_length * 60 * 60 * 1000,
                    });


                    acc += `${LocalizationsHelper.get('weekDaysCapArr')[day.day].toLowerCase()} ${timeFrom.format('HH:mm')}-${timeTo.format('HH:mm')}`;
                    return acc;
                }, ''),
                checked: index === 0,
                bottomContent: group.limitless_bonuses ? new DOMElement('div', {
                    class: 'f-checkout-group-gift-bonuses',
                    children: [
                        new DOMElement('template', {
                            html: `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                                        <path fill-rule="evenodd" clip-rule="evenodd" d="M7.55467 1.16795C6.67775 0.583339 5.80535 0.293468 5.1544 0.148813C4.82763 0.0761985 4.552 0.0391207 4.35259 0.0201293C4.25271 0.0106167 4.1714 0.00559044 4.11175 0.00293911C4.0819 0.0016126 4.05742 0.000878096 4.03868 0.000475049L4.0149 8.62481e-05L4.00631 1.73451e-05L4.00285 3.63603e-06L4.00063 0C4.00063 0 3.99997 0 3.99997 1C3.99997 2 3.99873 2 3.99873 2L3.99763 2L3.99568 2.00001C3.99964 2.0001 4.00885 2.00034 4.02295 2.00097C4.05118 2.00222 4.09879 2.00501 4.16297 2.01112C4.29168 2.02338 4.4848 2.0488 4.72054 2.10119C5.19459 2.20653 5.82218 2.41666 6.44527 2.83205C6.81987 3.08179 7.14104 3.50913 7.40031 4H2.33333C1.59667 4 1 4.67125 1 5.5V7H17V5.5C17 4.67125 16.4033 4 15.6667 4H10.5996C10.8589 3.50913 11.1801 3.08179 11.5547 2.83205C12.1778 2.41667 12.8053 2.20654 13.2794 2.10119C13.5151 2.04881 13.7082 2.02338 13.837 2.01113C13.9011 2.00501 13.9487 2.00223 13.977 2.00097C13.9911 2.00034 14.0002 2.00008 14.0041 1.99999L14.0023 2L14.0006 2C14.0006 2 14 2 14 1C14 4.52995e-06 13.9986 5.42417e-06 13.9986 5.42417e-06L13.9971 8.40441e-06L13.9936 2.21135e-05L13.985 9.10164e-05L13.9613 0.000479818C13.9425 0.000882864 13.918 0.00161737 13.8882 0.00294387C13.8285 0.00559521 13.7472 0.0106215 13.6473 0.020134C13.4479 0.0391254 13.1723 0.0762033 12.8455 0.148817C12.1946 0.293473 11.3222 0.583344 10.4453 1.16795C9.81467 1.58835 9.34503 2.18118 8.99997 2.76372C8.6549 2.18118 8.18526 1.58835 7.55467 1.16795ZM10 8H16V14C16 16.2091 14.2091 18 12 18C10.8053 18 9.73294 17.4762 9 16.6458C8.26705 17.4762 7.19469 18 6 18C3.79086 18 2 16.2091 2 14V8H8H10ZM12 16C10.8954 16 10 15.1046 10 14V10H14V14C14 15.1046 13.1046 16 12 16ZM4 10H8V14C8 15.1046 7.10457 16 6 16C4.89543 16 4 15.1046 4 14V10Z" fill="#7D2BCF"/>
                                    </svg>`
                        }),
                        new DOMElement('p', {
                            class: 'f-checkout-group-gift-bonuses-text',
                            text: LocalizationsHelper.get('page.course.sidebarWidget.giftBonusesGroup')
                        })
                    ]
                }) : undefined
            };
        });
    }

    /**
     * @param {PaymentInfoModel} paymentInfo
     * @param {boolean} reinit
     */
    _updateBalancesField(paymentInfo, reinit = false) {
        const balances = paymentInfo.balances;

        if (!balances || !Object.keys(balances).length) {
            if (this.$balancesField) {
                this.$balancesField.remove();
                this.$balancesField = null;
            }
            return;
        }

        const fieldConfig = {
            id: 'field-balances-checkout',
            inputId: 'input-balances-checkout',
            class: 'f-module_field',
            name: 'balances',
            label: LocalizationsHelper.get('component.checkout.balancesField.label'),
            options: Object.keys(balances).map((balanceType) => ({
                label: `${this._transformAmount(balances[balanceType].amount)} ${LocalizationsHelper.get(`component.checkout.balancesField.option.${balanceType}`)}`,
                value: balanceType,
                checked: balances[balanceType].selected
            }))
        };

        if (paymentInfo.bonusLimitExceeded) {
            fieldConfig.instructions = `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="14" height="14" rx="7" fill="#FED530"/>
                <path d="M7 2V8" stroke="white" stroke-width="2"/>
                <path d="M7 10V12" stroke="white" stroke-width="2"/>
                </svg> ${LocalizationsHelper.get('component.checkout.bonus.exceeded')} ${paymentInfo.bonusMaxPercent}% ${LocalizationsHelper.get('component.checkout.bonus.max')} (${this._transformAmount(paymentInfo.bonusLimitExceeded)})`;
        }

        const balancesField = new LmsCheckboxFieldElement(fieldConfig);

        if (this.$balancesField) {
            this.$balancesField.replaceWith(balancesField.getElement());
        } else {
            this.$promocodeField.parentNode.insertBefore(balancesField.getElement(), this.$promocodeField);
        }

        this.$balancesField = balancesField.getElement();

        if (reinit && this.formGroup) {
            const form = this.formGroup.getForm('checkout');

            if (form) {
                form.reinitField('balances', this.$balancesField);
            }
        }
    }

    /**
     * @param {PaymentInfoModel} paymentInfo
     */
    _updatePromoCodeField(paymentInfo) {
        if (!this.formGroup) {
            return;
        }

        const form = this.formGroup.getForm('checkout');

        if (!form || !paymentInfo.getCouponDiscount()) {
            return;
        }

        const promocodeField = form.getField('promocode');
        promocodeField.setApplied();
    }

    _updateAGPayButton() {
        if (!this.agPayButtonService) {
            return;
        }

        this.agPayButtonService.updatePaymentInfo(this._paymentInfo);
    }
}